import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { MutationKey, useQuery } from '@tanstack/react-query';
import { find, first, isEmpty, map, orderBy, size, some } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';

import { getCustomerResultsJobs } from 'api/customerResults';
import { DownloadFilesModal } from 'components/atoms/DownloadFilesModal';
import { JobDetails } from 'components/Pages/Jobs/JobDetails';
import { Permission } from 'interfaces/permissionOption';
import { formatDateTimeForUser } from 'utils/dateTime';
import { exportCustomerResults, exportLatestPerSlideCustomerResults } from 'utils/exports';
import useUsersById from 'utils/queryHooks/useUsersById';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import { getSelectablePrepareCustomerResultsJobs, prepareCustomerResultsUrlToFileToDownload } from './helpers';

interface SelectPrepareJobToExportModalProps {
  open: boolean;
  onClose: () => void;
  studyId: string;
}

const SelectPrepareJobToExportModal: React.FC<SelectPrepareJobToExportModalProps> = ({ open, onClose, studyId }) => {
  const { hasPermission } = usePermissions();
  const canViewUnpublishedResults = hasPermission(Permission.ViewUnpublishedResults);
  const { labId } = useCurrentLabId();

  const [jobIdSelection, setJobIdSelection] = useState<string | null>(null);
  const [onlyReturnFromWholeStudyJobs, setIncludeFilteredJobs] = useState(true);

  const isLatestJobWholeStudy = !jobIdSelection && onlyReturnFromWholeStudyJobs;
  const isLatestJobWithFiltersSelected = !jobIdSelection && !onlyReturnFromWholeStudyJobs;

  const selectedCustomerResultOption = isLatestJobWholeStudy
    ? 'LATEST_WHOLE_STUDY'
    : isLatestJobWithFiltersSelected
    ? 'LATEST_INCLUDING_FILTERED'
    : jobIdSelection;

  const { data: customerResultsJobs, isLoading: isLoadingCustomerResultsJobs } = useQuery({
    queryKey: ['customerResultsJobs', { studyId, labId }],
    queryFn: ({ signal }) => getCustomerResultsJobs({ studyId, labId }, signal),
    enabled: open,
  });

  const { getUserNameById } = useUsersById({
    enabled: open && some(customerResultsJobs, 'userId'),
  });

  const orderedJobs = orderBy(getSelectablePrepareCustomerResultsJobs(customerResultsJobs), 'startedAt', 'desc');

  const prepareCustomerResultsOptions = [
    {
      label: 'Latest prepared results (exported for the whole study)',
      value: 'LATEST_WHOLE_STUDY',
      job: find(orderedJobs, (job) => !job.params.queryObject),
    },
    {
      label: 'Latest prepared results (including not exported for the whole study)',
      value: 'LATEST_INCLUDING_FILTERED',
      job: first(orderedJobs),
    },
    ...map(orderedJobs, (job) => ({
      label: `${formatDateTimeForUser(job.startedAt)} - ${
        job.userId && canViewUnpublishedResults ? `Run by ${getUserNameById(job.userId) || 'Unknown User'} - ` : ''
      }${job.id}`,
      value: job.id,
      job,
    })),
  ];

  const selectedJobOption = find(
    prepareCustomerResultsOptions,
    (option) => option?.value === selectedCustomerResultOption
  );

  const jobId = selectedJobOption?.job?.id;

  const { enqueueSnackbar } = useSnackbar();
  const [urlsForDownload, setUrlsForDownload] = useState<string[] | null>(null);

  const onDownloadSuccess = (response: string[]) =>
    !isEmpty(response)
      ? setUrlsForDownload(response)
      : enqueueSnackbar(
          `No results available for the selected job "${
            selectedJobOption?.job?.name || selectedJobOption?.job?.id || selectedCustomerResultOption
          }".`,
          {
            variant: 'warning',
          }
        );

  const downloadCoordinatesMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'download coordinates for selected job',
    mutationKey: ['downloadCoordinates', { labId, studyId }] as MutationKey,
    mutationFn: () => exportCustomerResults({ labId, studyId, jobId }),
    onSuccess: onDownloadSuccess,
  });

  const downloadLatestBySlideCoordinatesMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'download latest per slide coordinates',
    mutationKey: ['downloadLatestBySlideCoordinates', { labId, studyId }] as MutationKey,
    mutationFn: () => exportLatestPerSlideCustomerResults({ labId, studyId }),
    onSuccess: onDownloadSuccess,
  });

  const isRunningMutations =
    downloadCoordinatesMutation.isLoading || downloadLatestBySlideCoordinatesMutation.isLoading;

  const sortedUrlsWithMatches = useMemo(() => {
    if (!urlsForDownload) return [];

    return orderBy(map(urlsForDownload, prepareCustomerResultsUrlToFileToDownload), 'zipNumber');
  }, [urlsForDownload]);

  return !urlsForDownload ? (
    <Dialog
      open={open}
      onClose={(event: any) => {
        event?.stopPropagation?.();
        onClose();
      }}
      onClick={(event) => event.stopPropagation()}
      maxWidth="md"
    >
      <DialogTitle>
        Select Results to Export
        {isRunningMutations && <CircularProgress size={24} />}
      </DialogTitle>
      <DialogContent dividers>
        <InputLabel>
          Select the job results to export
          {isLoadingCustomerResultsJobs ? ` (Loading...)` : ` (${size(customerResultsJobs)} jobs found)`}
        </InputLabel>
        <Select
          value={selectedCustomerResultOption}
          onChange={(event) => {
            if (event.target.value === 'LATEST_INCLUDING_FILTERED') {
              setJobIdSelection(null);
              setIncludeFilteredJobs(true);
              return;
            } else if (event.target.value === 'LATEST_WHOLE_STUDY') {
              setJobIdSelection(null);
              setIncludeFilteredJobs(false);
              return;
            } else {
              setJobIdSelection(event.target.value as string);
              setIncludeFilteredJobs(true);
            }
          }}
        >
          {map(prepareCustomerResultsOptions, (option) => (
            <MenuItem key={option?.value} value={option?.value}>
              <ListItemText primary={option?.label} />
            </MenuItem>
          ))}
        </Select>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3">Job Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isLoadingCustomerResultsJobs ? (
              <Typography variant="body1">Loading...</Typography>
            ) : selectedJobOption ? (
              selectedJobOption?.job ? (
                <JobDetails job={selectedJobOption?.job} />
              ) : (
                <Typography variant="body1">No job details available</Typography>
              )
            ) : (
              <Typography variant="body1">No job selected</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          onClick={() => {
            downloadLatestBySlideCoordinatesMutation.mutate();
          }}
          disabled={isRunningMutations}
        >
          Download Latest Per Slide Results
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            downloadCoordinatesMutation.mutate();
          }}
          disabled={!jobId || isRunningMutations}
        >
          Download Selected Job Results
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <DownloadFilesModal
      open={open}
      onClose={onClose}
      filesToDownload={sortedUrlsWithMatches}
      dialogTitle="Download Results"
    />
  );
};

export default SelectPrepareJobToExportModal;
